/**
 * 이슈 게시판 보드 — 목록·등록·상세 스레드·관리(라벨/상태) 일체.
 *
 * IssuesPage 전용이던 UI를 컴포넌트로 추출 (2026-08-12 Overview 개편):
 * 플랫폼 Overview 메인에 내장되고, /issues 페이지(북마크 호환)도 같은
 * 보드를 감싼다. 가시성·인가는 BFF 강제 — 여기는 표면만.
 */

import React, { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, ListItemText, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Dialog } from '@/shared/ui/overlays';
import { ConfirmDestructiveDialog } from '@/shared/ui/ConfirmDestructiveDialog';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import {
  addIssueComment,
  attachmentMarkdown,
  createIssue,
  deleteIssueComment,
  getIssue,
  listIssues,
  updateIssue,
  updateIssueComment,
  updateIssueLabels,
  updateIssueState,
  uploadIssueAttachment,
  type IssueCommentItem,
  type IssueItem,
} from '@/app/services/issue/issueService';
import { useAttachmentPolicy } from '@/app/services/issue/useAttachmentPolicy';
import {
  attachmentHint,
  attachmentTypeRefusal,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_TOO_LARGE_MESSAGE,
  insertAtCursor,
  replaceOnce,
  uploadPlaceholder,
} from '@/features/issues/attachmentComposer';
import { useAuthStore } from '@/app/stores/authStore';
import { canDeleteComment, canEditComment, canEditIssue } from '@/features/issues/issuePermissions';
import { formatDateTime } from '@/shared/dateTime';
import { LazyFallback } from '@/shared/ui/LazyFallback';

// 마크다운 렌더러(react-markdown·remark)는 진입 청크 밖(트랙 A-4) — 상세를 열 때 받는다
const IssueMarkdown = lazy(() => import('@/features/issues/IssueMarkdown'));

type StateFilter = 'open' | 'closed' | 'all';

const STATE_CHIP: Record<string, { label: string; color: 'success' | 'default' }> = {
  open: { label: 'Open', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
};


/** 업로드 자리표시자 일련번호 — 동시 업로드에서도 토큰이 겹치지 않게 */
let attachSeq = 0;

/**
 * 이미지 파일들을 업로드하고 본문 setter 에 마크다운 참조를 엮는다.
 * 자리표시자를 먼저 꽂고 완료 시 참조로 치환 — 업로드 중에도 계속 타이핑할
 * 수 있고, 실패하면 자리표시자만 걷어낸다.
 */
function attachImages(
  files: File[],
  caret: number | null,
  setValue: React.Dispatch<React.SetStateAction<string>>,
  beginUpload: () => () => void,
  types: readonly string[] | null,
): void {
  const accepted: File[] = [];
  for (const file of files) {
    // 받는 형식은 서버의 정책 — 모르면(답이 없으면) 미리 판정하지 않고 업로드의 거절에 맡긴다
    if (types && !types.includes(file.type)) {
      toast.error(attachmentTypeRefusal(types));
      continue;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error(ATTACHMENT_TOO_LARGE_MESSAGE);
      continue;
    }
    accepted.push(file);
  }
  if (accepted.length === 0) return;

  // 자리표시자 전부를 한 번에 순서대로 삽입 — 같은 caret 에 파일마다 반복
  // 삽입하면 뒤 파일이 앞에 끼어 역순이 된다 (리뷰 반영)
  const tokens = accepted.map(() => uploadPlaceholder((attachSeq += 1)));
  setValue((prev) => insertAtCursor(prev, tokens.join('\n'), caret));
  // 제출 잠금은 진행 중인 업로드 *상태*가 정한다 — begin 이 초안 키·세대를 캡처한 end 를
  // 돌려주므로, 초안이 교체된 뒤 늦게 정산되는 옛 업로드는 새 초안의 잠금을 건드리지 않는다.
  // 본문 글자(자리표시자)로 잠그면 지우면 풀리고 손으로 치면 잠긴다(게이트 리뷰 #498 2차).
  const ends = accepted.map(() => beginUpload());

  accepted.forEach((file, index) => {
    const token = tokens[index];
    void (async () => {
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const attachment = await uploadIssueAttachment(
          file.name || 'screenshot.png',
          file.type,
          bytes,
        );
        setValue((prev) => replaceOnce(prev, token, attachmentMarkdown(attachment.fileName, attachment.id)));
      } catch (err) {
        setValue((prev) => replaceOnce(prev, token, ''));
        toast.error(err instanceof Error ? err.message : '이미지 업로드에 실패했습니다');
      } finally {
        ends[index]();
      }
    })();
  });
}

function imageFiles(list: FileList | undefined | null): File[] {
  return Array.from(list ?? []).filter((f) => f.type.startsWith('image/'));
}

/** 붙여넣기/드래그 핸들러 묶음 — 등록 본문·댓글 입력에 동일 적용 */
function attachmentInputHandlers(
  setValue: React.Dispatch<React.SetStateAction<string>>,
  beginUpload: () => () => void,
  types: readonly string[] | null,
) {
  return {
    onPaste: (e: React.ClipboardEvent) => {
      const files = imageFiles(e.clipboardData?.files);
      if (files.length === 0) return; // 일반 텍스트 붙여넣기는 그대로
      e.preventDefault();
      const target = e.target as HTMLTextAreaElement;
      const caret = typeof target.selectionStart === 'number' ? target.selectionStart : null;
      attachImages(files, caret, setValue, beginUpload, types);
    },
    onDrop: (e: React.DragEvent) => {
      const all = Array.from(e.dataTransfer?.files ?? []);
      if (all.length === 0) return;
      // 파일 드롭은 무조건 기본 동작부터 차단 — 비이미지에서 브라우저가
      // 파일을 열며 작성 중 초안을 날리는 것 방지 (리뷰 반영)
      e.preventDefault();
      const files = all.filter((f) => f.type.startsWith('image/'));
      if (files.length === 0) {
        toast.error(attachmentTypeRefusal(types));
        return;
      }
      attachImages(files, null, setValue, beginUpload, types);
    },
    onDragOver: (e: React.DragEvent) => {
      // 기본 동작(브라우저가 파일을 여는 것)을 막아야 onDrop 이 온다
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    },
  };
}

const IssueBoard: React.FC = () => {
  const isAdmin = useAuthStore((s) => s.profile?.is_admin ?? false);
  const myId = useAuthStore((s) => s.profile?.id ?? '');
  /** 편집·삭제 버튼 노출은 서버와 같은 술어(issuePermissions)로 — 둘 다 이 값을 본다 */
  const viewer = React.useMemo(() => ({ id: myId, isAdmin }), [myId, isAdmin]);
  /** 서버가 받는 첨부 형식 — 모르면 null(고를 때 판정하지 않는다) */
  const attachTypes = useAttachmentPolicy();

  const [issues, setIssues] = useState<IssueItem[] | null>(null);
  const [nextToken, setNextToken] = useState('');
  /** 필터 전환 경합 가드 — 마지막 요청만 커밋 */
  const refreshGen = React.useRef(0);
  /** 상세 열기 경합 가드 — 마지막 선택만 다이얼로그에 커밋 */
  const detailGen = React.useRef(0);
  const [stateFilter, setStateFilter] = useState<StateFilter>('open');
  // 관리자의 방문 목적은 관리 — 기본을 전체 보기로 (일반 사용자는 항상 자기 것만)
  const [allUsers, setAllUsers] = useState(isAdmin);

  useEffect(() => {
    // 프로필(is_admin)이 뒤늦게 로드되는 첫 진입에서도 기본값이 관리자 기준을 따르게
    setAllUsers(isAdmin);
  }, [isAdmin]);
  const [error, setError] = useState<string | null>(null);

  // 등록 다이얼로그
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);
  // 상세 다이얼로그
  const [detail, setDetail] = useState<{ issue: IssueItem; comments: IssueCommentItem[] } | null>(
    null,
  );
  const [detailBusy, setDetailBusy] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  /** 이슈 제목·본문 편집 초안 — null 이면 보기 모드 */
  const [issueEdit, setIssueEdit] = useState<{ title: string; body: string } | null>(null);
  /** 댓글 편집 초안 — 한 번에 하나만 */
  const [commentEdit, setCommentEdit] = useState<{ id: string; body: string } | null>(null);
  /** 삭제 확인 대기 중인 댓글 — 되돌릴 수 없으니 한 번 더 묻는다 */
  const [confirmDelete, setConfirmDelete] = useState<IssueCommentItem | null>(null);
  /** 고친 초안을 버리는 동작 — 확인 뒤 실행할 것(다른 댓글 편집으로 전환·상세 닫기) */
  const [discardPrompt, setDiscardPrompt] = useState<(() => void) | null>(null);
  /**
   * 초안 키별 진행 중 업로드 — 제출·저장 잠금의 유일한 근거. 키: 'new' | 'comment' |
   * 'issueEdit' | 'commentEdit:<id>'. 초안이 교체되는 자리(discardDrafts·openDetail·저장
   * 성공)에서 키를 비우고 세대를 올리므로, 옛 초안의 늦은 정산은 새 초안을 건드리지 못한다.
   */
  const [pendingUploads, setPendingUploads] = useState<Record<string, number>>({});
  /** 초안 키별 세대 — 갱신 함수 밖(ref)에서 읽고 쓴다: setState 갱신 함수는 순수해야 한다 */
  const uploadGen = React.useRef<Record<string, number>>({});
  const isUploading = (draft: string) => (pendingUploads[draft] ?? 0) > 0;
  const beginUpload = useCallback((draft: string) => {
    const gen = (uploadGen.current[draft] ??= 0); // 키는 여기서 등록 — clearUploads 가 한 번도 안 비운 키도 본다
    setPendingUploads((m) => ({ ...m, [draft]: (m[draft] ?? 0) + 1 }));
    return () => {
      if ((uploadGen.current[draft] ?? 0) !== gen) return; // 초안이 교체됐다 — 옛 업로드의 정산은 무시
      setPendingUploads((m) => ({ ...m, [draft]: Math.max(0, (m[draft] ?? 0) - 1) }));
    };
  }, []);
  /** 초안 교체 — 그 키의 세대를 올리고 카운터를 비운다 */
  const clearUploads = useCallback((match: (draft: string) => boolean) => {
    for (const k of Object.keys(uploadGen.current)) if (match(k)) uploadGen.current[k] = (uploadGen.current[k] ?? 0) + 1;
    setPendingUploads((m) => {
      const next: typeof m = {};
      for (const [k, v] of Object.entries(m)) next[k] = match(k) ? 0 : v;
      return next;
    });
  }, []);
  /** 편집 초안의 setter — 초안이 닫혔으면 늦은 업로드 치환은 버려진다(prev 없음) */
  const setIssueEditBody: React.Dispatch<React.SetStateAction<string>> = useCallback((next) => {
    setIssueEdit((prev) => (prev ? { ...prev, body: typeof next === 'function' ? next(prev.body) : next } : prev));
  }, []);
  const setCommentEditBody: React.Dispatch<React.SetStateAction<string>> = useCallback((next) => {
    setCommentEdit((prev) => (prev ? { ...prev, body: typeof next === 'function' ? next(prev.body) : next } : prev));
  }, []);

  const refresh = useCallback(async () => {
    const gen = ++refreshGen.current;
    // 새 첫 페이지가 올 때까지 이전 커서를 무효화 — 리프레시 중 '더 보기'가
    // 낡은 커서를 새 필터에 보내는 창을 닫는다
    setNextToken('');
    try {
      const { issues: list, nextPageToken } = await listIssues({
        state: stateFilter === 'all' ? undefined : stateFilter,
        allUsers,
        pageSize: 50,
      });
      if (gen !== refreshGen.current) return; // 뒤늦은 응답이 현재 필터를 덮지 않게
      setIssues(list);
      setNextToken(nextPageToken);
      setError(null);
    } catch (err) {
      if (gen !== refreshGen.current) return;
      setError(err instanceof Error ? err.message : '이슈 목록을 불러오지 못했습니다');
      // 직전 필터의 행·커서를 남기면 현재 필터와 불일치한 화면이 된다 — 비운다
      setIssues([]);
      setNextToken('');
    }
  }, [stateFilter, allUsers]);

  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = useCallback(async () => {
    if (loadingMore) return; // 같은 커서 중복 요청(중복 행·키) 방지
    setLoadingMore(true);
    const gen = refreshGen.current;
    try {
      const { issues: more, nextPageToken } = await listIssues({
        state: stateFilter === 'all' ? undefined : stateFilter,
        allUsers,
        pageSize: 50,
        pageToken: nextToken,
      });
      if (gen !== refreshGen.current) return;
      setIssues((prev) => [...(prev ?? []), ...more]);
      setNextToken(nextPageToken);
    } catch (err) {
      if (gen !== refreshGen.current) return;
      toast.error(err instanceof Error ? err.message : '이슈를 더 불러오지 못했습니다');
    } finally {
      setLoadingMore(false);
    }
  }, [stateFilter, allUsers, nextToken, loadingMore]);

  useEffect(() => {
    // 필터·범위가 바뀐 순간 이전 결과를 로딩 상태로 비운다 — 새 응답 전까지
    // 다른 필터의 행이 보이지 않게. 액션 후 refresh() 직접 호출은 이 경로가
    // 아니므로 목록이 깜빡이지 않는다.
    setIssues(null);
    setNextToken('');
    void refresh();
  }, [refresh]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const issue = await createIssue(newTitle.trim(), newBody);
      toast.success(`#${issue.number} 이슈를 등록했습니다`);
      setCreateOpen(false);
      setNewTitle('');
      setNewBody('');
      clearUploads((k) => k === 'new');
      void refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '이슈 등록에 실패했습니다');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (issue: IssueItem) => {
    const gen = ++detailGen.current;
    try {
      const fetched = await getIssue(issue.id);
      if (gen !== detailGen.current) return; // 빠르게 다른 행을 연 경우 폐기
      setDetail(fetched);
      discardAllDrafts(); // 상세 진입도 초안 교체 자리 — 등록 초안('new')은 상세와 무관해 남는다
      setConfirmDelete(null);
      setDiscardPrompt(null);
      // 초안은 목록의 낡은 행이 아니라 방금 받은 상세 기준 — 낡은 라벨로 덮지 않게
      setLabelDraft(fetched.issue.labels.join(', '));
    } catch (err) {
      if (gen !== detailGen.current) return;
      toast.error(err instanceof Error ? err.message : '이슈를 불러오지 못했습니다');
    }
  };

  const withDetailRefresh = async (action: () => Promise<unknown>) => {
    if (!detail) return;
    setDetailBusy(true);
    try {
      await action();
    } catch (err) {
      // 변이 자체가 실패한 경우만 "실패"로 보고 — 재시도해도 안전
      toast.error(err instanceof Error ? err.message : '요청에 실패했습니다');
      setDetailBusy(false);
      return;
    }
    try {
      const fetched = await getIssue(detail.issue.id);
      setDetail(fetched);
      // 다른 관리자가 바꾼 라벨이 초안에 낡게 남지 않도록 같은 응답으로 동기화
      setLabelDraft(fetched.issue.labels.join(', '));
    } catch {
      // 반영은 됐고 되읽기만 실패 — 재시도 유도 대신 사실대로 알리고 닫는다
      toast.error('반영은 완료됐지만 화면 갱신에 실패했습니다 — 다시 열어 확인해 주세요');
      closeDetail(); // 상세를 떠나는 자리는 전부 이 함수를 지난다 — 오류 경로도 예외 아님
    } finally {
      void refresh();
      setDetailBusy(false);
    }
  };

  const handleComment = () =>
    withDetailRefresh(async () => {
      await addIssueComment(detail!.issue.id, commentBody.trim());
      setCommentBody('');
      clearUploads((k) => k === 'comment');
    });

  const handleToggleState = () =>
    withDetailRefresh(() =>
      updateIssueState(detail!.issue.id, detail!.issue.state === 'open' ? 'closed' : 'open'),
    );

  const handleCloseNotPlanned = () =>
    withDetailRefresh(() => updateIssueState(detail!.issue.id, 'closed', 'not_planned'));

  /**
   * 원문과 달라진 편집 초안(이슈·댓글)이 있는가 — 편집 전환(discardDrafts)이 버리는 것은 이것뿐이다
   * (게이트 리뷰 #498 결함 2)
   */
  const hasDirtyEditDraft = (() => {
    if (!detail) return false;
    if (issueEdit && (issueEdit.title !== detail.issue.title || issueEdit.body !== detail.issue.body)) return true;
    if (commentEdit) {
      const original = detail.comments.find((c) => c.id === commentEdit.id)?.body ?? '';
      if (commentEdit.body !== original) return true;
    }
    return false;
  })();
  /** 상세를 떠날 때(discardAllDrafts) 버려지는 전부 — 쓰다 만 새 댓글까지 */
  const hasDirtyDraft = hasDirtyEditDraft || (detail !== null && commentBody.trim() !== '');
  const discardDrafts = () => {
    setIssueEdit(null);
    setCommentEdit(null);
    clearUploads((k) => k === 'issueEdit' || k.startsWith('commentEdit:'));
  };
  /** 상세를 떠날 때 — 편집 초안뿐 아니라 쓰다 만 새 댓글도 버린다 */
  const discardAllDrafts = () => {
    discardDrafts();
    setCommentBody('');
    clearUploads((k) => k === 'comment');
  };
  /**
   * 초안을 버리게 되는 동작 — `dirty` 는 그 동작이 실제로 버리는 집합의 술어여야 한다
   * (편집 전환=hasDirtyEditDraft, 상세 닫기=hasDirtyDraft). 확인창은 "이 동작이 무엇을 지운다"는
   * 약속이라, 지우지 않는 것을 근거로 묻지 않는다(게이트 리뷰 #498 4차 B-1).
   */
  const guardDirty = (dirty: boolean, action: () => void) => {
    if (dirty) setDiscardPrompt(() => action);
    else action();
  };
  const closeDetail = () => {
    discardAllDrafts();
    setDetail(null);
  };

  const handleSaveIssue = () =>
    withDetailRefresh(async () => {
      if (!issueEdit) return;
      await updateIssue(detail!.issue.id, issueEdit.title.trim(), issueEdit.body);
      discardDrafts();
    });

  const handleSaveComment = () =>
    withDetailRefresh(async () => {
      if (!commentEdit) return;
      await updateIssueComment(detail!.issue.id, commentEdit.id, commentEdit.body.trim());
      discardDrafts();
    });

  const handleDeleteComment = () =>
    withDetailRefresh(async () => {
      if (!confirmDelete) return;
      await deleteIssueComment(detail!.issue.id, confirmDelete.id);
      setConfirmDelete(null);
      // 지우는 댓글을 편집 중이었다면 그 초안도 버린다 — 초안 폐기는 전부 discardDrafts 를 지난다
      if (commentEdit?.id === confirmDelete.id) discardDrafts();
    });

  const handleSaveLabels = () =>
    withDetailRefresh(() =>
      updateIssueLabels(
        detail!.issue.id,
        labelDraft
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    );

  return (
    <>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          select
          label="상태"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as StateFilter)}
          sx={{ width: 120 }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="all">전체</MenuItem>
        </TextField>
        {isAdmin && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={allUsers ? 'all' : 'mine'}
            onChange={(_, v: 'all' | 'mine' | null) => {
              if (v) setAllUsers(v === 'all');
            }}
          >
            <ToggleButton value="mine">내 이슈</ToggleButton>
            <ToggleButton value="all">전체 사용자</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={() => void refresh()}>
          새로고침
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          이슈 등록
        </Button>
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {issues === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : issues.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          표시할 이슈가 없습니다 — 문제나 건의가 있으면 이슈를 등록해 주세요.
        </Typography>
      ) : (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <List disablePadding>
            {issues.map((issue, idx) => (
              <React.Fragment key={issue.id}>
                <ListItemButton component="li" onClick={() => void openDetail(issue)} sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                          #{issue.number}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {issue.title}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={STATE_CHIP[issue.state]?.label ?? issue.state}
                          color={STATE_CHIP[issue.state]?.color ?? 'default'}
                          sx={{ height: 20 }}
                        />
                        {issue.labels.map((label) => (
                          <Chip key={label} size="small" label={label} sx={{ height: 20 }} />
                        ))}
                      </Stack>
                    }
                    secondary={
                      `${formatDateTime(issue.createdAt)}` +
                      (allUsers ? ` · ${issue.authorName || issue.authorId}` : '') +
                      (issue.commentCount > 0 ? ` · 댓글 ${issue.commentCount}` : '')
                    }
                  />
                </ListItemButton>
                {idx < issues.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      {nextToken && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? '불러오는 중…' : '더 보기'}
          </Button>
        </Box>
      )}

      {/* 등록 다이얼로그 */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>이슈 등록</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="제목"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="내용 (재현 방법·기대 동작 등)"
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            helperText={attachmentHint(attachTypes)}
            {...attachmentInputHandlers(setNewBody, () => beginUpload('new'), attachTypes)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={creating || isUploading('new') || !newTitle.trim()}
            startIcon={creating || isUploading('new') ? <CircularProgress size={14} /> : <AddIcon />}
          >
            {isUploading('new') ? '이미지 업로드 중…' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상세 다이얼로그 */}
      <Dialog open={detail !== null} onClose={() => !detailBusy && guardDirty(hasDirtyDraft, closeDetail)} fullWidth maxWidth="md">
        {detail && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {/* 명시적 닫기 — 바깥 클릭·ESC 만으로는 닫는 방법이 드러나지 않는다 */}
              <IconButton
                aria-label="닫기"
                onClick={() => guardDirty(hasDirtyDraft, closeDetail)}
                disabled={detailBusy}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" component="span" color="text.secondary">
                  #{detail.issue.number}
                </Typography>
                <Typography variant="h6" component="span">
                  {detail.issue.title}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    detail.issue.state === 'closed'
                      ? `Closed${detail.issue.stateReason === 'not_planned' ? ' (계획 없음)' : ''}`
                      : 'Open'
                  }
                  color={STATE_CHIP[detail.issue.state]?.color ?? 'default'}
                />
                {canEditIssue(detail.issue, viewer) && !issueEdit && (
                  <IconButton
                    size="small"
                    aria-label="이슈 편집"
                    disabled={detailBusy}
                    onClick={() => guardDirty(hasDirtyEditDraft, () => { discardDrafts(); setIssueEdit({ title: detail.issue.title, body: detail.issue.body }); })}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {detail.issue.authorName || detail.issue.authorId} · {formatDateTime(detail.issue.createdAt)}
                {detail.issue.editedAt && ` · 수정됨 ${formatDateTime(detail.issue.editedAt)}`}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                {issueEdit ? (
                  <Stack spacing={1.5}>
                    <TextField
                      fullWidth
                      label="제목"
                      value={issueEdit.title}
                      onChange={(e) => setIssueEdit({ ...issueEdit, title: e.target.value })}
                      disabled={detailBusy}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      label="본문"
                      value={issueEdit.body}
                      onChange={(e) => setIssueEdit({ ...issueEdit, body: e.target.value })}
                      disabled={detailBusy}
                      helperText={attachmentHint(attachTypes)}
                      {...attachmentInputHandlers(setIssueEditBody, () => beginUpload('issueEdit'), attachTypes)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={discardDrafts} disabled={detailBusy}>
                        취소
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void handleSaveIssue()}
                        disabled={detailBusy || isUploading('issueEdit') || !issueEdit.title.trim()}
                      >
                        {isUploading('issueEdit') ? '이미지 업로드 중…' : '저장'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : detail.issue.body ? (
                  <Suspense fallback={<LazyFallback height={72} />}><IssueMarkdown content={detail.issue.body} /></Suspense>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    (내용 없음)
                  </Typography>
                )}
              </Box>
              {detail.issue.labels.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  {detail.issue.labels.map((label) => (
                    <Chip key={label} size="small" label={label} />
                  ))}
                </Stack>
              )}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {/* 삭제된 댓글은 자리만 남고 수에 들지 않는다(서버 comment_count 와 같은 규칙) */}
                댓글 {detail.comments.filter((c) => !c.deleted).length}
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {detail.comments.map((comment) => (
                  <Paper key={comment.id} elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ flex: 1 }}>
                        {comment.authorName || comment.authorId} · {formatDateTime(comment.createdAt)}
                        {comment.editedAt && !comment.deleted && ` · 수정됨 ${formatDateTime(comment.editedAt)}`}
                      </Typography>
                      {canEditComment(comment, viewer) && commentEdit?.id !== comment.id && (
                        <IconButton
                          size="small"
                          aria-label="댓글 편집"
                          disabled={detailBusy}
                          onClick={() => guardDirty(hasDirtyEditDraft, () => { discardDrafts(); setCommentEdit({ id: comment.id, body: comment.body }); })}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDeleteComment(comment, viewer) && (
                        <IconButton
                          size="small"
                          aria-label="댓글 삭제"
                          disabled={detailBusy}
                          onClick={() => setConfirmDelete(comment)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {comment.deleted ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        삭제된 댓글
                      </Typography>
                    ) : commentEdit?.id === comment.id ? (
                      <Stack spacing={1}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="댓글 편집 내용"
                          value={commentEdit.body}
                          onChange={(e) => setCommentEdit({ ...commentEdit, body: e.target.value })}
                          disabled={detailBusy}
                          helperText={attachmentHint(attachTypes)}
                          {...attachmentInputHandlers(setCommentEditBody, () => beginUpload(`commentEdit:${comment.id}`), attachTypes)}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={discardDrafts} disabled={detailBusy}>
                            취소
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => void handleSaveComment()}
                            disabled={detailBusy || isUploading(`commentEdit:${comment.id}`) || !commentEdit.body.trim()}
                          >
                            {isUploading(`commentEdit:${comment.id}`) ? '이미지 업로드 중…' : '저장'}
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Suspense fallback={<LazyFallback height={40} />}><IssueMarkdown content={comment.body} /></Suspense>
                    )}
                  </Paper>
                ))}
                {detail.comments.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    아직 댓글이 없습니다.
                  </Typography>
                )}
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="댓글 작성"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                disabled={detailBusy}
                helperText={attachmentHint(attachTypes)}
                {...attachmentInputHandlers(setCommentBody, () => beginUpload('comment'), attachTypes)}
              />
              {isAdmin && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="라벨 (쉼표 구분 — 예: bug, in progress)"
                    value={labelDraft}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    disabled={detailBusy}
                  />
                  <Button size="small" onClick={() => void handleSaveLabels()} disabled={detailBusy}>
                    라벨 저장
                  </Button>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              {detail.issue.state === 'open' && (
                <Button color="inherit" onClick={() => void handleCloseNotPlanned()} disabled={detailBusy}>
                  계획 없음으로 닫기
                </Button>
              )}
              <Button onClick={() => void handleToggleState()} disabled={detailBusy}>
                {detail.issue.state === 'open' ? '완료로 닫기' : '다시 열기'}
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleComment()}
                disabled={detailBusy || isUploading('comment') || !commentBody.trim()}
              >
                {isUploading('comment') ? '이미지 업로드 중…' : '댓글 등록'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 댓글 삭제 확인 — 공용 파괴 확인 표면(pending 동안 재진입·닫기 잠금). 자리 표시만 남는다 */}
      <ConfirmDestructiveDialog
        open={confirmDelete !== null}
        title="댓글 삭제"
        message='이 댓글을 삭제할까요? 화면에서는 "삭제된 댓글" 자리만 남고 본문은 보이지 않습니다.'
        confirmLabel="삭제"
        pending={detailBusy}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => void handleDeleteComment()}
      />

      {/* 편집 초안 버리기 확인 — 다른 댓글 편집으로 전환·상세 닫기가 고친 초안을 말없이 버리지 않게 */}
      <ConfirmDestructiveDialog
        open={discardPrompt !== null}
        title="편집 취소"
        message="편집 중인 내용을 버릴까요? 저장하지 않은 수정이 사라집니다."
        confirmLabel="버리기"
        pending={false}
        onCancel={() => setDiscardPrompt(null)}
        onConfirm={() => {
          const action = discardPrompt;
          setDiscardPrompt(null);
          action?.();
        }}
      />
    </>
  );
};

export default IssueBoard;
